import { FCElement } from '@fc/component';
import { isMobileBreakpoint, debounce } from '@fc/utilities';

class ShowMoreComponent extends FCElement {
  requiredRefs = ['showMoreButton', 'showMoreItems', 'showMoreContent'];

  #open = false;
  #desktopOff = false;
  #h0 = 0;
  #cls = 'hidden';
  #anim = null;
  #ac = null;

  static ANIMATION_DURATION = 300;

  get #isMobile() {
    return isMobileBreakpoint();
  }

  connectedCallback() {
    super.connectedCallback();

    this.#ac = new AbortController();
    this.#syncBreakpoint();

    window.addEventListener(
      'resize',
      debounce(() => this.#syncBreakpoint(), 150),
      { signal: this.#ac.signal, passive: true },
    );

    this.refs.showMoreButton?.setAttribute('aria-expanded', 'false');
    this.#h0 = this.refs.showMoreContent?.offsetHeight ?? 0;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#anim?.cancel();
    this.#anim = null;
    this.#ac?.abort();
    this.#ac = null;
  }

  #syncBreakpoint = () => {
    this.#desktopOff = this.dataset.disableOnDesktop === 'true';
    this.#cls = this.#desktopOff ? 'mobile:hidden' : 'hidden';
  };

  #reveal = () => {
    const { showMoreItems, showMoreContent } = this.refs;

    this.#h0 = showMoreContent.offsetHeight;
    const from = this.#h0;

    showMoreItems?.forEach((el) => {
      el instanceof HTMLElement && el.classList.remove(this.#cls);
    });

    // trigger reflow so scrollHeight reflects revealed items
    void showMoreContent.offsetHeight;

    return { from, to: showMoreContent.scrollHeight };
  };

  #conceal = () => ({
    from: this.refs.showMoreContent.offsetHeight,
    to: this.#h0,
  });

  #runAnimation = (from, to) => {
    const container = this.refs.showMoreContent;

    if (from === to) {
      this.#settle();
      return;
    }

    container.style.overflow = 'hidden';
    this.#anim?.cancel();

    this.#anim = container.animate(
      { height: [`${from}px`, `${to}px`] },
      { duration: ShowMoreComponent.ANIMATION_DURATION, easing: 'ease-in-out' },
    );

    this.#anim.onfinish = () => this.#settle();
    this.#anim.oncancel = () => {
      container.style.removeProperty('height');
      container.style.overflow = '';
    };
  };

  #settle() {
    const { showMoreContent, showMoreItems } = this.refs;
    if (!showMoreContent || !showMoreItems) return;

    // when collapsing, re-hide items after the animation ends
    if (this.#open) {
      showMoreItems.forEach((el) => {
        el instanceof HTMLElement && el.classList.add(this.#cls);
      });
    }

    showMoreContent.style.removeProperty('height');
    showMoreContent.style.overflow = '';
    this.#open = !this.#open;
    this.#anim = null;
  }

  toggle = (event) => {
    if (!event) return;
    event.preventDefault();

    this.#syncBreakpoint();

    if (!this.#isMobile && this.#desktopOff) return;

    const expanding = !this.#open;
    const { from, to } = expanding ? this.#reveal() : this.#conceal();

    const state = expanding ? 'true' : 'false';
    this.dataset.expanded = state;
    this.refs.showMoreButton.setAttribute('aria-expanded', state);

    this.#runAnimation(from, to);
  };
}

if (!customElements.get('reveal-more')) {
  customElements.define('reveal-more', ShowMoreComponent);
}
